import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Panel,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';

import JobNode from './nodes/JobNode';
import MachineNode from './nodes/MachineNode';
import DispatcherNode from './nodes/DispatcherNode';
import DependencyEdge from './nodes/DependencyEdge';
import JobsTableModal from './nodes/JobsTableModal';
import OptimizationSidebar from './OptimizationSidebar';
import WorkflowControls from './WorkflowControls';
import VersionLabel from './VersionLabel';
import {
  GlobalConfigProvider,
  readWcOptimizeEnabled,
  isWcOptimizeEnabledFor,
} from '../contexts/GlobalConfigContext';
import './WorkflowCanvas.css';
import { getLayoutedElements } from '../utils/elkLayout';

const nodeTypes = {
  jobNode: JobNode,
  machineNode: MachineNode,
  dispatcherNode: DispatcherNode,
};

const edgeTypes = {
  dependencyEdge: DependencyEdge,
};

const defaultEdgeOptions = {
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
  style: { strokeWidth: 2 },
};

const WorkflowCanvas = () => {

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedNodeType, setSelectedNodeType] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [machines, setMachines] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [machineDependencies, setMachineDependencies] = useState([]);
  const [viewMode, setViewMode] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState({ current: 0, total: 0, currentMachine: '' });
  const [timeLimit, setTimeLimit] = useState(30);
  
  // Dependency modal states
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [dependencyJobs, setDependencyJobs] = useState([]);
  const [dependencyModalTitle, setDependencyModalTitle] = useState('');

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Logout handler
  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        // Redirect to login page
        window.location.href = '/login.html';
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect even if there's an error
      window.location.href = '/login.html';
    }
  };

  // Utility function to create dispatcher node
  // Function to handle main panel progress updates from DispatcherNode
  const handleOptimizationStart = useCallback((totalMachines) => {
    console.log('🎯 Starting main panel progress tracking for', totalMachines, 'machines');
    setIsOptimizing(true);
    setOptimizationProgress({ current: 0, total: totalMachines, currentMachine: '' });
  }, []);

  const handleOptimizationProgress = useCallback((current, total, machineName) => {
    console.log(`📊 Main panel progress: ${current}/${total} - ${machineName}`);
    setOptimizationProgress({ current, total, currentMachine: machineName });
  }, []);

  const handleOptimizationComplete = useCallback(() => {
    console.log('✅ Main panel optimization tracking complete');
    setIsOptimizing(false);
    setOptimizationProgress({ current: 0, total: 0, currentMachine: '' });
    
    // Update dispatcher nextOptimization timer
    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === 'dispatcherNode') {
          return {
            ...node,
            data: {
              ...node.data,
              nextOptimization: Date.now() + 60000, // Next optimization in 60 seconds
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Handler for dependency edge clicks
  const handleDependencyEdgeClick = useCallback(async (edgeData) => {
    console.log('📊 Dependency edge clicked:', edgeData);
    
    try {
      const response = await fetch(
        `/api/machine-dependencies/jobs?fromMachineId=${edgeData.fromMachineId}&toMachineId=${edgeData.toMachineId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch dependency jobs');
      }
      
      const jobs = await response.json();
      
      setDependencyJobs(jobs);
      setDependencyModalTitle(
        `Dependency: ${edgeData.fromMachineName} → ${edgeData.toMachineName} (${jobs.length} jobs)`
      );
      setShowDependencyModal(true);
      
    } catch (error) {
      console.error('Error fetching dependency jobs:', error);
      alert('Failed to load dependency jobs. Please try again.');
    }
  }, []);

  // Handler to persist machine updates (capabilities, description) to DB
  const handleMachineUpdate = useCallback(async (machineId, updates) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/machines/${machineId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        console.error('Failed to save machine update:', await response.text());
      } else {
        console.log(`✅ Machine ${machineId} updated:`, updates);
      }
    } catch (error) {
      console.error('Error saving machine update:', error);
    }
  }, []);

  // Utility function to create dispatcher node
  const createDispatcherNode = useCallback((id, jobsData = []) => ({
    id,
    type: 'dispatcherNode',
    position: { x: 1000, y: -350 }, 
    data: {
      label: 'Job Dispatcher',
      connected: true,
      pendingJobs: jobsData.filter(j => j.status === 'pending').length,
      processingJobs: jobsData.filter(j => j.status === 'running').length,
      completedJobs: jobsData.filter(j => j.status === 'completed').length,
      lastUpdate: Date.now(),
      throughput: jobsData.length > 0 ? 24 : 0,
      efficiency: 87,
      nextOptimization: Date.now() + 30000,
      onOptimizeAll: handleOptimizationStart,
      onProgressUpdate: handleOptimizationProgress,
      onOptimizationComplete: handleOptimizationComplete,
    },
    draggable: true,
  }), [handleOptimizationStart, handleOptimizationProgress, handleOptimizationComplete]);



  // Utility function to create edges with dependency edge support
  const createEdges = useCallback((dispatcherId, machineNodes, dependenciesData = []) => {
    const dispatcherEdges = machineNodes.map((machine) => ({
      id: `dispatcher-${machine.id}`,
      source: dispatcherId,
      target: machine.id,
      label: 'job assignment',
      type: 'smoothstep',
      sourceHandle: 'jobs-out',
      targetHandle: 'jobs-in',
    }));

    let machineDepEdges = [];
    if (dependenciesData.length > 0) {
      const currentMachineIds = new Set(machineNodes.map(m => m.id));
      
      machineDepEdges = dependenciesData
        .filter(dep => {
          const sourceId = `machine-${dep.from_machine_id}`;
          const targetId = `machine-${dep.to_machine_id}`;
          return currentMachineIds.has(sourceId) && currentMachineIds.has(targetId);
        })
        .map(dep => ({
          id: `machine-dep-${dep.from_machine_id}-${dep.to_machine_id}`,
          source: `machine-${dep.from_machine_id}`,
          target: `machine-${dep.to_machine_id}`,
          label: `${dep.dependency_count} jobs`,
          type: 'dependencyEdge',
          animated: false,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 25,
            height: 25,
          },
          style: { 
            strokeWidth: 3, 
            stroke: '#ff6b6b' 
          },
          data: {
            fromMachineId: dep.from_machine_id,
            fromMachineName: dep.from_machine_name,
            toMachineId: dep.to_machine_id,
            toMachineName: dep.to_machine_name,
            dependencyCount: dep.dependency_count,
            onEdgeClick: handleDependencyEdgeClick
          }
        }));
    }

    return [...dispatcherEdges, ...machineDepEdges];
  }, [handleDependencyEdgeClick]);

  // Load minimal workflow with only dispatcher
  const loadSampleWorkflow = useCallback(() => {
    const dispatcherNode = createDispatcherNode('dispatcher-1');
    setNodes([dispatcherNode]);
    setEdges([]);
    setMachines([]);
    setIsLoading(false);
  }, [createDispatcherNode, setNodes, setEdges]);

  // Fetch and load all data
  // Fetch and load all data with ELK auto-layout
const loadAllData = useCallback(async () => {
  setIsLoading(true);
  
  try {
    console.log('=== Loading real data ===');

    // Load all data in parallel
    const [jobsResponse, machinesResponse, depsResponse] = await Promise.allSettled([
      fetch('/api/jobs?limit=100000').then(res => res.ok ? res.json() : []),
      fetch('/api/machines').then(res => res.ok ? res.json() : []),
      fetch('/api/machine-dependencies').then(res => res.ok ? res.json() : [])
    ]);

    const jobsData = jobsResponse.status === 'fulfilled' ? jobsResponse.value : [];
    const machinesData = machinesResponse.status === 'fulfilled' ? machinesResponse.value : [];
    const dependenciesData = depsResponse.status === 'fulfilled' ? depsResponse.value : [];

    console.log(`✓ Loaded ${jobsData.length} jobs, ${machinesData.length} machines, ${dependenciesData.length} dependencies`);

    // Create machines and dispatcher nodes
    let allNodes = [];
    let allEdges = [];

    if (machinesData.length > 0) {
      // Create machine nodes (ignore database positions, ELK will handle layout)
      const machineNodes = machinesData.map((machine, index) => ({
        id: `machine-${machine.id}`,
        type: 'machineNode',
        position: { x: 0, y: 0 }, // Temporary position, ELK will override
        data: {
          label: machine.name || `Machine ${index + 1}`,
          type: machine.type || 'WORK_CENTER',
          status: machine.status === 'available' ? 'idle' : machine.status || 'idle',
          capabilities: machine.capabilities || [],
          machineId: machine.id,
          assignedJobs: parseInt(machine.assigned_jobs) || 0,
          workload: parseInt(machine.total_workload) || 0,
          description: machine.description || '',
          pendingJobs: parseInt(machine.assigned_jobs) || 0,
          scheduledJobs: Math.floor((parseInt(machine.assigned_jobs) || 0) * 0.6),
          timeSaved: 0,  // Will be calculated after actual optimization
          costSaved: 0,  // Will be calculated after actual optimization
          optimizationAlgorithm: ['Genetic Algorithm', 'Simulated Annealing', 'Ant Colony', 'Round Robin'][index % 4],
          utilization: Math.min(95, Math.floor((parseInt(machine.total_workload) || 0) / 1000)),
          isOptimizing: false,
          triggerOptimization: false,
          onUpdate: (updates) => handleMachineUpdate(machine.id, updates),
        },
        draggable: true,
      }));
      
      // Create dispatcher node (temporary position)
      const dispatcherNode = createDispatcherNode('dispatcher-main', jobsData);
      dispatcherNode.position = { x: 0, y: 0 };
      
      // Create edges
      allEdges = createEdges('dispatcher-main', machineNodes, dependenciesData);
      
      // Combine all nodes
      allNodes = [dispatcherNode, ...machineNodes];
      
      console.log('🎨 Applying ELK auto-layout...');
      
      // Apply ELK layout
      const { nodes: layoutedNodes, edges: layoutedEdges } = await getLayoutedElements(allNodes, allEdges);
      
      console.log('✅ ELK layout applied successfully');
      
      // Set the layouted nodes and edges
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      setMachines(machinesData);
      
    } else {
      // No machines - just create dispatcher
      const dispatcherNode = createDispatcherNode('dispatcher-main', jobsData);
      setNodes([dispatcherNode]);
      setEdges([]);
      setMachines([]);
    }

    // Load workflows
    try {
      const workflowResponse = await fetch('/api/workflows');
      if (workflowResponse.ok) {
        const workflowData = await workflowResponse.json();
        setWorkflows(workflowData);
      }
    } catch (error) {
      console.log('Workflows API not available');
    }

  } catch (error) {
    console.error('Error loading data:', error);
    loadSampleWorkflow();
  } finally {
    setIsLoading(false);
  }
}, [createDispatcherNode, createEdges, loadSampleWorkflow, setNodes, setEdges]);
  // Initial data load
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Event listener for Excel import completion from dispatcher
  useEffect(() => {
    const handleImportCompleted = (event) => {
      console.log('Excel import completed from dispatcher:', event.detail);
      // Refresh the canvas data after import
      loadAllData();
    };

    window.addEventListener('excel-path-import-completed', handleImportCompleted);
    
    return () => {
      window.removeEventListener('excel-path-import-completed', handleImportCompleted);
    };
  }, [loadAllData]);

  const handleRefreshData = useCallback(() => {
    console.log('Manual refresh triggered');
    loadAllData();
  }, [loadAllData]);

  // File drag and drop handlers
  const handleFileDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleFileDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileDrop = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    const excelFile = files.find(file => 
      file.name.toLowerCase().match(/\.(xlsx|xlsm|xls)$/)
    );

    if (!excelFile) {
      alert('Please drop an Excel file (.xlsx, .xlsm, .xls)');
      return;
    }

    setIsProcessingFile(true);

    try {
      // Get sheet names
      const formData = new FormData();
      formData.append('file', excelFile);

      const sheetResponse = await fetch('/api/excel/sheet-names', {
        method: 'POST',
        body: formData,
      });

      if (!sheetResponse.ok) {
        throw new Error('Failed to read Excel file');
      }

      const sheetData = await sheetResponse.json();
      
      // Handle sheet selection
      let selectedSheet = sheetData.sheets[0];
      if (sheetData.sheets.length > 1) {
        selectedSheet = prompt(
          `Multiple sheets found: ${sheetData.sheets.join(', ')}\n\nEnter sheet name to import:`,
          sheetData.sheets[0]
        );
        
        if (!selectedSheet || !sheetData.sheets.includes(selectedSheet)) {
          throw new Error('Invalid sheet selection');
        }
      }

      // Import Excel data
      const importFormData = new FormData();
      importFormData.append('file', excelFile);
      importFormData.append('sheetName', selectedSheet);

      const importResponse = await fetch('/api/import/excel-direct', {
        method: 'POST',
        body: importFormData,
      });

      if (!importResponse.ok) {
        throw new Error('Failed to import Excel data');
      }

      const importResult = await importResponse.json();
      console.log('Excel import completed:', importResult);

      // Refresh data
      await loadAllData();

      alert(`Import successful!\nJobs Created: ${importResult.summary.jobsCreated}\nMachines Created: ${importResult.summary.machinesCreated}`);

    } catch (error) {
      console.error('Error processing file:', error);
      alert(`Failed to process file: ${error.message}`);
    } finally {
      setIsProcessingFile(false);
    }
  }, [loadAllData]);

  const createNode = useCallback((type, position) => {
    const id = `${type}-${Date.now()}`;
    
    if (type === 'dispatcher') {
      return {
        id,
        type: 'dispatcherNode',
        position,
        data: {
          label: 'New Dispatcher',
          connected: true,
          pendingJobs: 0,
          processingJobs: 0,
          completedJobs: 0,
          updateInterval: 5000,
          lastUpdate: Date.now(),
          throughput: 0,
          efficiency: 85,
          nextOptimization: Date.now() + 60000,
        },
      };
    }

    if (type === 'machine') {
      return {
        id,
        type: 'machineNode',
        position,
        data: {
          label: 'New Machine',
          type: 'GENERIC',
          status: 'idle',
          capabilities: [],
          pendingJobs: 0,
          scheduledJobs: 0,
          timeSaved: 0,
          costSaved: 0,
          optimizationAlgorithm: 'Round Robin',
          utilization: 0,
          isOptimizing: false,
          machineId: `new-${Date.now()}`,
          assignedJobs: 0,
          workload: 0,
          description: '',
          onUpdate: (updates) => console.log('New machine update (not yet saved):', updates),
        },
      };
    }
    
    return {
      id,
      type: 'machineNode',
      position,
      data: {
        label: 'New Node',
        status: 'idle',
        pendingJobs: 0,
        scheduledJobs: 0,
      },
    };
  }, []);

  const onConnect = useCallback((params) => {
    const newEdge = {
      ...params,
      ...defaultEdgeOptions,
      id: `${params.source}-${params.target}`,
      label: 'dependency',
      type: 'smoothstep',
    };
    setEdges((eds) => addEdge(newEdge, eds));
  }, [setEdges]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    if (!reactFlowInstance) return;

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');

    if (typeof type === 'undefined' || !type) {
      return;
    }

    const position = reactFlowInstance.project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    });

    const newNode = createNode(type, position);
    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes, createNode]);

  const handleRunOptimization = async () => {
    console.log('🚀 Starting global optimization...');
    setIsOptimizing(true);
    
    // Skip the work centers whose header checkbox is unticked — the same
    // selection the dispatcher's optimize button uses.
    const wcSelection = readWcOptimizeEnabled();
    const machineNodes = nodes
      .filter(n => n.type === 'machineNode')
      .filter(n => isWcOptimizeEnabledFor(wcSelection, n.data?.label));
    const totalMachines = machineNodes.length;

    if (totalMachines === 0) {
      alert('No work centers are selected. Tick at least one work center\'s checkbox to optimize it.');
      setIsOptimizing(false);
      return;
    }

    console.log(`Found ${totalMachines} machine nodes to optimize`);
    console.log('Setting isOptimizing to true and progress to:', { current: 0, total: totalMachines, currentMachine: '' });
    setOptimizationProgress({ current: 0, total: totalMachines, currentMachine: '' });
    
    // Process each machine sequentially to show progress
    for (let i = 0; i < machineNodes.length; i++) {
      const machine = machineNodes[i];
      const machineName = machine.data.label || `Machine ${i + 1}`;
      
      // Update progress
      console.log(`📊 Progress: ${i + 1}/${totalMachines} - ${machineName}`);
      setOptimizationProgress({ 
        current: i + 1, 
        total: totalMachines, 
        currentMachine: machineName 
      });
      
      // Trigger optimization on current machine
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === machine.id) {
            return {
              ...node,
              data: {
                ...node.data,
                isOptimizing: true,
                triggerOptimization: Date.now(),
                status: 'optimizing'
              },
            };
          }
          return node;
        })
      );
      
      // Wait for this machine's optimization to complete (simulate API call)
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds per machine
      
      // Complete optimization for this machine
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === machine.id) {
            return {
              ...node,
              data: {
                ...node.data,
                isOptimizing: false,
                status: 'scheduled',
                timeSaved: (node.data.timeSaved || 0) + Math.floor(Math.random() * 30 + 10),
                costSaved: (node.data.costSaved || 0) + Math.floor(Math.random() * 200 + 50),
                scheduledJobs: (node.data.scheduledJobs || 0) + Math.floor(Math.random() * 3 + 1),
                pendingJobs: Math.max(0, (node.data.pendingJobs || 0) - Math.floor(Math.random() * 2 + 1))
              },
            };
          }
          return node;
        })
      );
    }

    try {
      // Call backend optimization API for global results
      const response = await fetch('/api/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          machines: machineNodes,
          timestamp: Date.now(),
          timeLimit: timeLimit
        }),
      });
      
      // Check for saved files notification  
      if (response.ok) {
        const result = await response.json();
        if (result.saved_files) {
          console.log('📁 Global optimization files saved:', result.saved_files);
          console.log(`TXT: ${result.saved_files.txt_file.split('/').pop()}`);
          console.log(`CSV: ${result.saved_files.csv_file.split('/').pop()}`);
        }
      }

    } catch (error) {
      console.error('Error running optimization:', error);
    }
    
    // Reset states and update dispatcher nextOptimization
    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === 'dispatcherNode') {
          return {
            ...node,
            data: {
              ...node.data,
              nextOptimization: Date.now() + 60000, // Next optimization in 60 seconds
            },
          };
        }
        return node;
      })
    );
    
    setIsOptimizing(false);
    setOptimizationProgress({ current: 0, total: 0, currentMachine: '' });
  };

  const handleStopOptimization = () => {
    setIsOptimizing(false);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === 'machineNode') {
          return {
            ...node,
            data: {
              ...node.data,
              isOptimizing: false,
              status: 'idle'
            },
          };
        }
        return node;
      })
    );
  };

  const handleSaveWorkflow = async () => {
    try {
      const workflowData = {
        name: `Optimization Layout ${new Date().toLocaleDateString()}`,
        description: `Created from optimization canvas - ${nodes.filter(n => n.type === 'machineNode').length} machines`,
        job_ids: nodes.filter(n => n.type === 'jobNode').map(n => n.id),
        canvas_data: {
          nodes: nodes,
          edges: edges,
          viewport: reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 },
          viewMode: viewMode
        },
        created_by: 'optimization-canvas'
      };

      const response = await fetch('/api/workflows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workflowData),
      });

      if (response.ok) {
        const savedWorkflow = await response.json();
        setCurrentWorkflow(savedWorkflow);
        console.log('Optimization layout saved:', savedWorkflow);
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
    }
  };

  const handleLoadWorkflow = async (workflowId) => {
    try {
      const response = await fetch(`/api/workflows/${workflowId}`);
      if (response.ok) {
        const workflow = await response.json();
        const canvasData = workflow.canvas_data;

        if (canvasData.nodes) setNodes(canvasData.nodes);
        if (canvasData.edges) setEdges(canvasData.edges);
        if (canvasData.viewMode) setViewMode(canvasData.viewMode);
        if (canvasData.viewport && reactFlowInstance) {
          reactFlowInstance.setViewport(canvasData.viewport);
        }

        setCurrentWorkflow(workflow);
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
    }
  };

  const handleClearCanvas = () => {
    setNodes([]);
    setEdges([]);
    setCurrentWorkflow(null);
  };

  const handleAutoLayout = async () => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = 
      await getLayoutedElements(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  };

  if (isLoading) {
    return (
      <GlobalConfigProvider>
        <div className="workflow-canvas-container">
          <div className="loading-indicator">
            <h3>Loading optimization system...</h3>
            <p>Please wait while we initialize the dispatcher and load machines.</p>
          </div>
        </div>
      </GlobalConfigProvider>
    );
  }

  return (
    <GlobalConfigProvider>
      <div className="workflow-canvas-container">
        <ReactFlowProvider>
          <div 
            className="workflow-canvas" 
            ref={reactFlowWrapper}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            connectionLineStyle={{ stroke: '#ff6b6b', strokeWidth: 2 }}
            deleteKeyCode="Delete"
            multiSelectionKeyCode="Shift"
            fitView
          >
            <Controls showInteractive={false} />
            <MiniMap
              nodeStrokeColor={(n) => n.type === 'machineNode' ? '#ff6b6b' : n.type === 'dispatcherNode' ? '#667eea' : '#4ecdc4'}
              nodeColor={(n) => n.type === 'machineNode' ? '#ff6b6b' : n.type === 'dispatcherNode' ? '#667eea' : '#4ecdc4'}
              nodeBorderRadius={8}
            />
            <Background color="#aaa" gap={16} />

            {/* Logout button in top-right (only show if auth is enabled) */}
            {process.env.REACT_APP_DISABLE_AUTH !== 'true' && (
              <Panel position="top-right">
                <button 
                  onClick={handleLogout}
                  style={{
                    padding: '8px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#5568d3'}
                  onMouseOut={(e) => e.target.style.background = '#667eea'}
                >
                  🚪 Logout
                </button>
              </Panel>
            )}

            {isDragOver && (
              <Panel position="top-center" style={{ pointerEvents: 'none' }}>
                <div className="file-drop-overlay">
                  <div className="drop-zone">
                    <div className="drop-icon">📁</div>
                    <div className="drop-text">Drop Excel file to import</div>
                  </div>
                </div>
              </Panel>
            )}

            {isProcessingFile && (
              <Panel position="top-center" style={{ pointerEvents: 'none' }}>
                <div className="processing-overlay">
                  <div className="processing-spinner">⚙️</div>
                  <div className="processing-text">Processing Excel file...</div>
                </div>
              </Panel>
            )}           

          </ReactFlow>
        </div>

        {showSidebar && (
          <OptimizationSidebar
            machines={machines}
            timeLimit={timeLimit}
            onTimeLimitChange={setTimeLimit}
            onDragStart={(event, nodeType) => {
              event.dataTransfer.setData('application/reactflow', nodeType);
              event.dataTransfer.effectAllowed = 'move';
            }}
            onQuickCreate={handleRunOptimization}
          />
        )}

        {/* Version label in top-left corner */}
        <VersionLabel />
        
        {/* Dependency Jobs Modal */}
        {showDependencyModal && (
          <JobsTableModal
            isOpen={showDependencyModal}
            onClose={() => setShowDependencyModal(false)}
            jobs={dependencyJobs}
            title={dependencyModalTitle}
            mode="dependency"
          />
        )}
      </ReactFlowProvider>
    </div>
    </GlobalConfigProvider>
  );
};

export default WorkflowCanvas;